import React, { useState, useEffect } from 'react';
import './CourseQuestionForm.css';

const CourseQuestionForm = ({ sectionId, onQuestionSaved, onCancel, editingQuestion = null }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        problemStatement: '',
        videoLink: '',
        solution: '',
        solutionCode: '',
        initialCode: '',
        sourceLanguage: 'java',
        testCases: '',
        expectedOutput: '',
        constraints: '',
        examples: '',
        followUp: '',
        relatedTopics: '',
        difficultyLevel: 'EASY',
        questionOrder: 1,
        isPremium: false,
        timeLimitSeconds: 60,
        memoryLimitMB: 128
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (editingQuestion) {
            setFormData({
                title: editingQuestion.title || '',
                description: editingQuestion.description || '',
                problemStatement: editingQuestion.problemStatement || '',
                videoLink: editingQuestion.videoLink || '',
                solution: editingQuestion.solution || '',
                solutionCode: editingQuestion.solutionCode || '',
                initialCode: editingQuestion.initialCode || '',
                sourceLanguage: editingQuestion.sourceLanguage || 'java',
                testCases: editingQuestion.testCases || '',
                expectedOutput: editingQuestion.expectedOutput || '',
                constraints: editingQuestion.constraints || '',
                examples: editingQuestion.examples || '',
                followUp: editingQuestion.followUp || '',
                relatedTopics: editingQuestion.relatedTopics || '',
                difficultyLevel: editingQuestion.difficultyLevel || 'EASY',
                questionOrder: editingQuestion.questionOrder || 1,
                isPremium: editingQuestion.isPremium || false,
                timeLimitSeconds: editingQuestion.timeLimitSeconds || 60,
                memoryLimitMB: editingQuestion.memoryLimitMB || 128
            });
        }
    }, [editingQuestion]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const questionData = {
                ...formData,
                questionOrder: parseInt(formData.questionOrder),
                timeLimitSeconds: parseInt(formData.timeLimitSeconds),
                memoryLimitMB: parseInt(formData.memoryLimitMB),
                courseSection: { id: sectionId }
            };

            const url = editingQuestion
                ? `/api/course-questions/${editingQuestion.id}`
                : '/api/course-questions';

            const method = editingQuestion ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(questionData),
            });

            if (response.ok) {
                const savedQuestion = await response.json();
                console.log('Question saved successfully:', savedQuestion);
                onQuestionSaved(savedQuestion);
                setFormData({
                    title: '',
                    description: '',
                    problemStatement: '',
                    videoLink: '',
                    solution: '',
                    solutionCode: '',
                    initialCode: '',
                    sourceLanguage: 'java',
                    testCases: '',
                    expectedOutput: '',
                    constraints: '',
                    examples: '',
                    followUp: '',
                    relatedTopics: '',
                    difficultyLevel: 'EASY',
                    questionOrder: 1,
                    isPremium: false,
                    timeLimitSeconds: 60,
                    memoryLimitMB: 128
                });
            } else {
                const errorText = await response.text();
                console.error('Failed to save question. Status:', response.status);
                console.error('Error response:', errorText);
                setError(`Failed to save question: ${errorText}`);
            }
        } catch (error) {
            console.error('Network error saving question:', error);
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getSampleInitialCode = () => {
        const sampleCode = `// Sample Initial Code Template
// Customize this for your specific question

// JavaScript Example:
function solve(input) {
    // TODO: Implement your solution here
    // Replace 'input' with your actual parameter names
    
    return null; // Replace with actual return value
}

// Java Example:
public class Solution {
    public String solve(String input) {
        // TODO: Implement your solution here
        // Replace 'String input' with your actual parameter types and names
        
        return null; // Replace with actual return value
    }
    
    public static void main(String[] args) {
        Solution solution = new Solution();
        // Add test cases here
        System.out.println(solution.solve("test input"));
    }
}

// Python Example:
class Solution:
    def solve(self, input):
        # TODO: Implement your solution here
        # Replace 'input' with your actual parameter names
        
        pass  # Replace with actual implementation

# Test your solution
if __name__ == "__main__":
    solution = Solution()
    print(solution.solve("test input"))

// C++ Example:
#include <iostream>
#include <string>
using namespace std;

class Solution {
public:
    string solve(string input) {
        // TODO: Implement your solution here
        // Replace 'string input' with your actual parameter types and names
        
        return ""; // Replace with actual return value
    }
};

int main() {
    Solution solution;
    // Add test cases here
    cout << solution.solve("test input") << endl;
    return 0;
}`;
        return sampleCode;
    };

    return (
        <div className="course-question-form">
            <h3>{editingQuestion ? 'Edit Question' : 'Add New Question'}</h3>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="title">Question Title *</label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="difficultyLevel">Difficulty Level</label>
                        <select
                            id="difficultyLevel"
                            name="difficultyLevel"
                            value={formData.difficultyLevel}
                            onChange={handleInputChange}
                        >
                            <option value="EASY">Easy</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="HARD">Hard</option>
                            <option value="EXPERT">Expert</option>
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description *</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        rows="3"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="problemStatement">Problem Statement</label>
                    <textarea
                        id="problemStatement"
                        name="problemStatement"
                        value={formData.problemStatement}
                        onChange={handleInputChange}
                        rows="5"
                        placeholder="Describe the problem in detail..."
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="videoLink">Video Link</label>
                        <input
                            type="url"
                            id="videoLink"
                            name="videoLink"
                            value={formData.videoLink}
                            onChange={handleInputChange}
                            placeholder="https://example.com/video"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="questionOrder">Question Order</label>
                        <input
                            type="number"
                            id="questionOrder"
                            name="questionOrder"
                            value={formData.questionOrder}
                            onChange={handleInputChange}
                            min="1"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="solution">Solution Explanation</label>
                    <textarea
                        id="solution"
                        name="solution"
                        value={formData.solution}
                        onChange={handleInputChange}
                        rows="4"
                        placeholder="Explain the solution approach..."
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="solutionCode">Solution Code</label>
                    <textarea
                        id="solutionCode"
                        name="solutionCode"
                        value={formData.solutionCode}
                        onChange={handleInputChange}
                        rows="6"
                        placeholder="// Write your solution code here..."
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="initialCode">Initial Code (IDE Starting Code)</label>
                    <div className="form-actions" style={{justifyContent: 'flex-start', marginTop: '10px', marginBottom: '15px'}}>
                        <button
                            type="button"
                            onClick={() => setFormData({...formData, initialCode: getSampleInitialCode()})}
                            className="template-button"
                        >
                            📝 Load Sample Template
                        </button>
                    </div>
                    <textarea
                        id="initialCode"
                        name="initialCode"
                        value={formData.initialCode}
                        onChange={handleInputChange}
                        rows="6"
                        placeholder={`// Write the starting code that will appear in the IDE for this question
// Example for JavaScript:
function solve(nums, target) {
    // TODO: Implement your solution
    return [];
}

// Example for Java:
public class Solution {
    public int[] twoSum(int[] nums, int target) {
        // TODO: Implement your solution
        return new int[]{};
    }
}

// Leave empty to use default templates`}
                    />
                    <small className="form-help">
                        This code will be the starting point for students in the IDE. 
                        If left empty, a generic template will be used.
                    </small>
                </div>

                <div className="form-group">
                    <label htmlFor="sourceLanguage">Source Language for Initial Code</label>
                    <select
                        id="sourceLanguage"
                        name="sourceLanguage"
                        value={formData.sourceLanguage}
                        onChange={handleInputChange}
                        className="form-control"
                    >
                        <option value="java">Java</option>
                        <option value="python">Python</option>
                        <option value="javascript">JavaScript</option>
                        <option value="cpp">C++</option>
                        <option value="c">C</option>
                        <option value="csharp">C#</option>
                        <option value="php">PHP</option>
                        <option value="ruby">Ruby</option>
                        <option value="swift">Swift</option>
                        <option value="go">Go</option>
                        <option value="rust">Rust</option>
                        <option value="kotlin">Kotlin</option>
                        <option value="typescript">TypeScript</option>
                    </select>
                    <small className="form-help">
                        Select the programming language you're writing the initial code in. 
                        The IDE will automatically convert this code to the student's selected language.
                    </small>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="testCases">Test Cases</label>
                        <textarea
                            id="testCases"
                            name="testCases"
                            value={formData.testCases}
                            onChange={handleInputChange}
                            rows="4"
                            placeholder="Input: [1,2,3]\nOutput: 6"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="expectedOutput">Expected Output</label>
                        <textarea
                            id="expectedOutput"
                            name="expectedOutput"
                            value={formData.expectedOutput}
                            onChange={handleInputChange}
                            rows="4"
                            placeholder="Describe expected output..."
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="constraints">Constraints</label>
                        <textarea
                            id="constraints"
                            name="constraints"
                            value={formData.constraints}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="1 ≤ n ≤ 10^5"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="examples">Examples</label>
                        <textarea
                            id="examples"
                            name="examples"
                            value={formData.examples}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="Example 1: Input: [1,2,3], Output: 6"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="timeLimitSeconds">Time Limit (seconds)</label>
                        <input
                            type="number"
                            id="timeLimitSeconds"
                            name="timeLimitSeconds"
                            value={formData.timeLimitSeconds}
                            onChange={handleInputChange}
                            min="1"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="memoryLimitMB">Memory Limit (MB)</label>
                        <input
                            type="number"
                            id="memoryLimitMB"
                            name="memoryLimitMB"
                            value={formData.memoryLimitMB}
                            onChange={handleInputChange}
                            min="1"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="relatedTopics">Related Topics</label>
                    <input
                        type="text"
                        id="relatedTopics"
                        name="relatedTopics"
                        value={formData.relatedTopics}
                        onChange={handleInputChange}
                        placeholder="Arrays, Strings, Dynamic Programming"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="followUp">Follow-up Questions</label>
                    <textarea
                        id="followUp"
                        name="followUp"
                        value={formData.followUp}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="What if the input size is larger? What if we need to optimize further?"
                    />
                </div>

                <div className="form-group checkbox-group">
                    <label>
                        <input
                            type="checkbox"
                            name="isPremium"
                            checked={formData.isPremium}
                            onChange={handleInputChange}
                        />
                        Premium Content
                    </label>
                </div>

                <div className="form-actions">
                    <button type="submit" disabled={loading} className="btn-primary">
                        {loading ? 'Saving...' : (editingQuestion ? 'Update Question' : 'Add Question')}
                    </button>
                    <button type="button" onClick={onCancel} className="btn-secondary">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CourseQuestionForm; 
